import {createContext, useCallback, useContext, useReducer,} from "react";
import {
  BOX_COMPOSITIONS,
  DEFAULT_PRICING_PLAN,
  DEFAULT_PROMO_CODE,
} from "~/constants/globals";
import {defaultAnswers} from "~/utils/quiz/questionsData";
import {deleteIntroSessionProducts, setIntroPersistance} from "~/utils/quiz/introPersistanceHandler";

// Action types
const ACTIONS = {
  SET_ANSWER: "SET_ANSWER",
  SET_BOX_COMPOSITION: "SET_BOX_COMPOSITION",
  SET_PRICING_PLAN: "SET_PRICING_PLAN",
  SET_BOX_FILLING_RESPONSE: "SET_BOX_FILLING_RESPONSE",
  SET_FWC_QUANTITY: "SET_FWC_QUANTITY",
  SET_STATE_CODE: "SET_STATE_CODE",
  SET_USER_DATA: "SET_USER_DATA",
  SET_USER_EMAIL: "SET_USER_EMAIL",
  SET_CART: "SET_CART",
  SET_PRODUCTS: "SET_PRODUCTS",
  UPDATE_STATE: "UPDATE_STATE",
};

// Initial state
export const DEFAULT_STATE = {
  data: {
    code: DEFAULT_PROMO_CODE,
  },
  settings: {
    answers: defaultAnswers,
    quizToken: null,
    deviceId: null,
    sessionId: null,
    promoCode: DEFAULT_PROMO_CODE,
    pricingPlanSlug: DEFAULT_PRICING_PLAN,
    skipEmail: false,
    fwcQuantity: 0,
    stateCode: null, // GeoIP or default is set by box-filling-response.js
    boxComposition: BOX_COMPOSITIONS.DEFAULT,
    clubFrequency: 4,
    skuList: null,
    pack: null,
    boostTaxons: null,
    repulseTaxons: null,
  },
  session: {
    selectedProducts: null,
    cart: null,
    cartId: null,
    customerId: null,
    userEmail: null,
    subscriptionStatus: null,
    subscriptionsData: null,
    setupCompleted: false,
    resultsShown: false // TODO: we need to update theme to use this new prop
  }
};

// Reducer function
function quizReducer(state = DEFAULT_STATE, action) {
  let resetStorage = false;
  let newState = null;

  switch (action.type) {
    case ACTIONS.UPDATE_STATE: {
      newState = {
        ...state,
        data: {
          ...state.data,
          ...(action.payload.data || {}),
        },
        settings: {
          ...state.settings,
          ...(action.payload.settings || {}),
        },
        session: {
          ...state.session,
          ...(action.payload.session || {}),
        }
      };
      // if no selectedProducts are coming from caller, reset storage
      if (action.payload.session?.selectedProducts === null) {
        resetStorage = true;
      }

      break;
    }
    case ACTIONS.SET_ANSWER: {
      const {questionId, value, resetProducts} = action.payload;
      const hasAnswerChanged = JSON.stringify(state.settings.answers[questionId]) !== JSON.stringify(value);
      newState = {
        ...state,
        settings: {
          ...state.settings,
          answers: {
            ...state.settings.answers,
            [questionId]: value,
          },
        }
      };

      // Persist immediately for critical actions
      if (hasAnswerChanged && resetProducts) {
        newState.session.selectedProducts = null;
        resetStorage = true;
      }
      break;
    }

    case ACTIONS.SET_BOX_COMPOSITION: {
      newState = {
        ...state,
        settings: {
          ...state.settings,
          boxComposition: action.payload,
        }
      };
      resetStorage = true;
      break;
    }

    case ACTIONS.SET_BOX_FILLING_RESPONSE: {
      const {selectedProducts, quizToken, stateCode, deviceId, sessionId, cart, subscriptionsData} = action.payload;
      newState = {
        ...state,
        settings: {
          ...state.settings,
          quizToken,
          stateCode,
          deviceId,
          sessionId
        },
        session: {
          ...state.session,
          selectedProducts,
          cart,
          cartId: cart?.id,
          subscriptionsData,
          resultsShown: true,
        }
      };

      resetStorage = true;
      break;
    }

    case ACTIONS.SET_FWC_QUANTITY: {
      newState = {
        ...state,
        settings: {
          ...state.settings,
          fwcQuantity: action.payload,
        }
      };
      resetStorage = true;
      break;
    }

    case ACTIONS.SET_STATE_CODE: {
      newState = {
        ...state,
        settings: {
          ...state.settings,
          stateCode: action.payload
        },
      };
      resetStorage = true;
      break;
    }
    case ACTIONS.SET_CART: {
      newState = {
        ...state,
        session: {
          ...state.session,
          cart: action.payload,
          cartId: action.payload?.id
        }
      };
      break;
    }
    case ACTIONS.SET_PRODUCTS: {
      newState = {
        ...state,
        session: {
          ...state.session,
          selectedProducts: action.payload,
        }
      };
      break;
    }
    case ACTIONS.SET_USER_EMAIL: {
      newState = {
        ...state,
        session: {
          ...state.session,
          userEmail: action.payload,
        }
      };
      break;
    }
    case ACTIONS.SET_USER_DATA: {
      newState = {
        ...state,
        session: {
          ...state.session,
          customerId: action.payload.customerId,
          subscriptionStatus: action.payload.subscriptionStatus,
        }
      };
      break;
    }

    default:
      return state;
  }

  if (resetStorage) {
    // we need to explicitly delete since setIntroPersistance wont account for null values
    deleteIntroSessionProducts();
  }

  setIntroPersistance(newState);
  return newState;
}

// Create context
const QuizContext = createContext();

// Provider component
export function QuizProvider({children}) {
  const [state, dispatch] = useReducer(quizReducer, DEFAULT_STATE);

  // Action creators
  const setAnswer = useCallback((questionId, value) => {
    dispatch({
      type: ACTIONS.SET_ANSWER,
      payload: {questionId, value},
    });
  }, []);

  const setBoxComposition = useCallback((boxComposition) => {
    dispatch({type: ACTIONS.SET_BOX_COMPOSITION, payload: boxComposition});
  }, []);

  const setBoxFillingResponse = useCallback((payload) => {
    dispatch({
      type: ACTIONS.SET_BOX_FILLING_RESPONSE,
      payload,
    });
  }, []);

  const setFwcQuantity = useCallback((fwcQuantity) => {
    dispatch({type: ACTIONS.SET_FWC_QUANTITY, payload: fwcQuantity});
  }, []);

  const setStateCode = useCallback((stateCode) => {
    dispatch({type: ACTIONS.SET_STATE_CODE, payload: stateCode});
  }, []);

  const setUserData = useCallback(({customerId, subscriptionStatus}) => {
    dispatch({
      type: ACTIONS.SET_USER_DATA,
      payload: {customerId, subscriptionStatus},
    });
  }, []);

  const setUserEmail = useCallback((email) => {
    dispatch({
      type: ACTIONS.SET_USER_EMAIL,
      payload: email,
    });
  }, []);

  const setCart = useCallback((cart) => {
    dispatch({
      type: ACTIONS.SET_CART,
      payload: cart,
    });
  }, []);

  const setProducts = useCallback((newProducts) => {
    dispatch({
      type: ACTIONS.SET_PRODUCTS,
      payload: newProducts,
    });
  }, []);

  const updateState = useCallback((payload) => {
    dispatch({
      type: ACTIONS.UPDATE_STATE,
      payload,
    });
  }, []);

  const value = {
    // State
    ...state,
    // Actions
    setAnswer,
    setBoxComposition,
    setBoxFillingResponse,
    setFwcQuantity,
    setStateCode,
    setUserData,
    setUserEmail,
    setCart,
    setProducts,
    updateState
  };

  return <QuizContext.Provider value={value}>{children}</QuizContext.Provider>;
}

// Custom hook to use quiz context
export function useQuizState() {
  const context = useContext(QuizContext);
  if (!context) {
    throw new Error("useQuizState must be used within a QuizProvider");
  }
  return context;
}

// Selector hook for specific quiz data
export function useQuizSelector(selector) {
  const {quizData} = useQuizState();
  return selector(quizData);
}
